import '@/components/ChatWidget/ChatList/styles.scss';

import { ChatRoomType, Message, Room, useChatState } from '@/hooks/useSocket/chat/useChatRooms';
import React, { HTMLAttributes, useState } from 'react';
import { getClasses, getResultFromState } from '@/utils';

import { Accordion } from 'react-bootstrap';
import ChatListContent from '@/components/ChatWidget/ChatList/ChatListContent';
import ChatListHeader from '@/components/ChatWidget/ChatList/ChatListHeader';

type ChatListProps = HTMLAttributes<HTMLDivElement> & {
  rooms: Record<string, Room>;
  onSelect: (roomId: string) => void;
  height: number;
};

const ChatList = ({ rooms, onSelect, height, ...props }: ChatListProps) => {
  const [activeRoom, setActiveRoom] = useState(undefined);
  const handleSelect = (roomId: string) => {
    setActiveRoom(roomId);
    onSelect(roomId);
  };
  return (
    <div {...props} className={getClasses('ChatList', props?.className)}>
      <Accordion onSelect={handleSelect}>
        {Object.entries(rooms)?.map(([roomId, room]) => {
          return (
            <Accordion.Item eventKey={roomId} key={roomId}>
              <ChatRoom
                active={activeRoom === roomId}
                name={room?.displayName}
                avatar={room?.displayImageUrl}
                iata={room?.trip?.servicerIataAirlineCode}
                scheduled={room?.trip?.tripScheduled}
                latest={room?.latestMessage}
                roomId={roomId}
                eventKey={roomId}
                unrepliedCount={room?.unreadCount}
                roomType={room?.roomType}
                height={height}
              />
            </Accordion.Item>
          );
        })}
      </Accordion>
    </div>
  );
};

export const useChatRoom = (
  roomId: string,
  type: ChatRoomType
): [
  Record<string, Room>[string],
  (room: Record<string, Room>[string] | ((room: Record<string, Room>[string]) => Record<string, Room>[string])) => void,
] => {
  const [room, setChatState] = useChatState(({ state, setState }) => [state.rooms[type][roomId], setState]);
  const setRoom = (room: Record<string, Room>[string] | ((room: Record<string, Room>[string]) => Record<string, Room>[string])) =>
    setChatState((current) => ({ ...current, rooms: { ...current.rooms, [roomId]: getResultFromState(room, current.rooms[roomId]) } }));
  return [room, setRoom];
};

const ChatRoom = ({
  name,
  avatar,
  iata,
  scheduled,
  latest,
  roomId,
  eventKey,
  active,
  unrepliedCount,
  roomType,
  height,
}: {
  name: string;
  avatar: string;
  iata: string;
  scheduled: string;
  latest: Message;
  roomId: string;
  eventKey: string;
  active: boolean;
  unrepliedCount: number;
  roomType: ChatRoomType;
  height: number;
}) => {
  return (
    <>
      <ChatListHeader
        name={name}
        displayImageUrl={avatar}
        servicerIataAirlineCode={iata}
        scheduledTime={scheduled}
        latestMessage={latest}
        roomId={roomId}
        eventKey={eventKey}
        unread={unrepliedCount}
        roomType={roomType}
      />
      {active && <ChatListContent roomId={roomId} roomType={roomType} height={height} />}
    </>
  );
};

export default ChatList;
